import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useReveal } from "./mocks";
import "./business.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SLIDES = [
  { tag: "Get started", title: "Create your business account", body: "Reach new clients, control every booking, and keep the tools you already use — set up in about ten minutes.", img: "biz_step_1.png" },
  { tag: "Business profile", title: "Tell us about your business", body: "Add your name, services, cover photo and logo. It becomes your public Marova profile that clients discover and book from.", img: "biz_step_2.png" },
  { tag: "Location & hours", title: "Show clients where to find you", body: "Drop your address on the map, set your weekly hours, and choose whether guests come to you or you travel to them.", img: "biz_step_3.png" },
  { tag: "Team", title: "Add the people who provide your services", body: "Bring in your providers and manage their schedules, services and bookings in one place — or run it solo.", img: "biz_step_5.png" },
  { tag: "Services", title: "List the services clients see", body: "Set each service with its category, price, duration and providers — exactly how it appears when a guest books.", img: "biz_step_4.png" },
  { tag: "Booking setup", title: "Choose how clients can book", body: "Offer Instant Book, Request to Book, or mix both per service. You approve what you want, automate the rest.", img: "biz_step_6.png" },
  { tag: "Integrations", title: "Connect the tools you already use", body: "Sync Google, Apple or Outlook calendars and your scheduling system to prevent double bookings.", img: "biz_step_7.png" },
  { tag: "Review", title: "Review before you go live", body: "Preview your public profile exactly as clients will see it, confirm every detail, then publish with one tap.", img: "biz_step_8.png" },
  { tag: "You're live", title: "Start receiving clients", body: "Your dashboard brings requests, today's schedule and revenue together — beauty moves business.", img: "biz_step_9.png" },
];

export default function ForBusiness({ spaStyle }) {
  const [ref, cls] = useReveal();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = SLIDES.length;

  const go = useCallback((i) => setIndex(((i % n) + n) % n), [n]);

  const touchX = useRef(null);
  const onTouchStart = (e) => { setPaused(true); touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 45) go(index + (dx < 0 ? 1 : -1));
    touchX.current = null;
    setPaused(false);
  };

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % n), 6000);
    return () => clearInterval(t);
  }, [paused, n]);

  const active = SLIDES[index];

  return (
    <section id="business" className="lp-dark" data-testid="for-business" style={{ position: "relative", padding: "140px 64px", overflow: "hidden", scrollMarginTop: 20 }}>
      <img src={`${BACKEND_URL}/api/files/aura_hero_${spaStyle}.png`} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", opacity: .12 }} />

      <div ref={ref} className={cls} style={{ position: "relative", maxWidth: 1200, margin: "0 auto", textAlign: "center" }}>
        <div className="eyebrow" style={{ color: "var(--blush)" }}>marova business</div>
        <h2 className="serif" style={{ fontSize: "clamp(40px, 5vw, 64px)", fontWeight: 500, margin: "12px auto 0", lineHeight: 1, maxWidth: 780 }}>
          Here's how Marova Business works.
        </h2>
        <p style={{ fontSize: 17, lineHeight: 1.65, color: "rgba(251,248,247,.7)", maxWidth: 560, margin: "22px auto 0" }}>
          From first sign-up to a live, bookable profile — the full setup for your spa or salon, one step at a time.
        </p>
      </div>

      <div className="bizc" data-testid="biz-carousel"
           onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
           onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div className="bizc-stage">
          <div className="bizc-bar">
            <span className="bizc-bar-brand"><span className="dot" />Marova Business · Setup</span>
            <span className="bizc-bar-count" data-testid="biz-counter">{String(index + 1).padStart(2, "0")} / {String(n).padStart(2, "0")}</span>
          </div>
          <div className="bizc-progress"><i style={{ width: `${((index + 1) / n) * 100}%` }} /></div>

          <div className="bizc-view">
            <div className="bizc-track" style={{ transform: `translateX(-${index * 100}%)` }}>
              {SLIDES.map((s, i) => (
                <div key={s.img} className={`bizc-slide${i === index ? " on" : ""}`} data-testid={`biz-slide-${i + 1}`}>
                  <div className="bizc-copy">
                    <span className="bizc-step"><i>{i + 1}</i>{s.tag}</span>
                    <h3 className="serif">{s.title}</h3>
                    <p>{s.body}</p>
                  </div>
                  <div className="bizc-shot">
                    <div className="frame">
                      <img src={`${BACKEND_URL}/api/files/${s.img}`} alt={s.title} loading={i <= 1 ? "eager" : "lazy"} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button className="bizc-arrow prev" onClick={() => go(index - 1)} data-testid="biz-prev" aria-label="Previous">
            <ChevronLeft size={22} />
          </button>
          <button className="bizc-arrow next" onClick={() => go(index + 1)} data-testid="biz-next" aria-label="Next">
            <ChevronRight size={22} />
          </button>
        </div>

        <div className="bizc-dots" data-testid="biz-dots">
          {SLIDES.map((s, i) => (
            <button key={s.img} className={i === index ? "on" : ""} onClick={() => go(i)}
                    data-testid={`biz-dot-${i + 1}`} aria-label={`Go to ${s.tag}`} />
          ))}
        </div>
      </div>

      <div style={{ position: "relative", maxWidth: 1200, margin: "56px auto 0", textAlign: "center" }}>
        <a href="#waitlist" data-testid="business-cta" className="btn" style={{ borderColor: "var(--porcelain)", color: "var(--porcelain)" }} onClick={() => window.dispatchEvent(new CustomEvent("marova:role", { detail: "business" }))}>Join as a business</a>
      </div>
    </section>
  );
}
